import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Page } from "@/components/layout/Layout";
import { ListingCard } from "@/components/common/Cards";
import { Loading, Empty, ErrorBox } from "@/components/common/States";
import FilterPanel from "@/components/listings/FilterPanel";
import { useCategory } from "@/hooks/useMeta";
import SEO from "@/components/common/SEO";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const cat = useCategory(params.get("category"));
  const set = (patch, resetSpecs = false) => {
    const next = new URLSearchParams(params);
    if (resetSpecs) [...next.keys()].filter((k) => k.startsWith("f_") || k === "condition").forEach((k) => next.delete(k));
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const qs = params.toString();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["listings", qs],
    queryFn: async () => (await api.get(`/listings?${qs}`)).data,
  });
  const panel = <FilterPanel params={params} set={set} reset={() => setParams({})} />;

  const categoryName = cat?.name;
  const pageTitle = categoryName ? `${categoryName} İlanları` : "Tüm FPV Parça İlanları";
  const pageDesc = categoryName 
    ? `${categoryName} kategorisindeki ikinci el ve sıfır FPV parçaları, teknik özellikler ve fiyat karşılaştırmaları.` 
    : "Güncel FPV motor, FC, ESC, VTX ve drone parça ilanlarını filtreleyin, karşılaştırın ve güvenle satın alın.";

  return (
    <Page>
      <SEO
        title={pageTitle}
        description={pageDesc}
        canonical={`https://createfpv.com/ilanlar${qs ? `?${qs}` : ""}`}
      />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">İlanlar</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold">{cat ? cat.name : "Tüm FPV ilanları"}</h1>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <Input data-testid="search-input" placeholder="Model, marka veya başlık ara" defaultValue={params.get("q") || ""}
            onKeyDown={(e) => e.key === "Enter" && set({ q: e.target.value })} className="sm:w-72" />
          <Select value={params.get("sort") || "new"} onValueChange={(v) => set({ sort: v })}>
            <SelectTrigger className="w-40" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">En yeni</SelectItem>
              <SelectItem value="price_asc">Fiyat: artan</SelectItem>
              <SelectItem value="price_desc">Fiyat: azalan</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild><Button variant="outline" className="lg:hidden border-black/[0.08] bg-transparent" data-testid="mobile-filter-button"><SlidersHorizontal className="h-4 w-4" /></Button></SheetTrigger>
            <SheetContent side="left" className="overflow-y-auto bg-white pt-10"><SheetTitle className="sr-only">Filtreler</SheetTitle>{panel}</SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block panel h-fit p-5 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto">{panel}</aside>
        <section>
          {isLoading && <Loading />}
          {isError && <ErrorBox message="İlanlar yüklenemedi" />}
          {data && (
            <>
              <p className="mb-4 text-sm text-zinc-500" data-testid="results-count">{data.total} ilan bulundu</p>
              {params.get("province") && data.other_locations_count > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-[#0071e3]/40 bg-[#0071e3]/10 p-3 text-sm" data-testid="other-locations-notice">
                  Diğer il/ilçelerde bu kriterlere uyan {data.other_locations_count} ilan daha var.
                  <Button size="sm" variant="outline" className="border-[#0071e3] bg-transparent" data-testid="expand-location-button" onClick={() => set({ province: "", district: "" })}>Aramayı tüm Türkiye'ye genişlet</Button>
                </div>
              )}
              {data.items.length === 0 ? <Empty hint="Filtreleri genişletmeyi deneyin" /> : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="listing-results">
                  {data.items.map((l) => <ListingCard key={l.id} item={l} />)}
                </div>
              )}
              {data.total > data.items.length * (Number(params.get("page") || 1)) && (
                <div className="mt-6 flex justify-center gap-2">
                  {Number(params.get("page") || 1) > 1 && <Button variant="outline" className="bg-transparent" onClick={() => set({ page: String(Number(params.get("page")) - 1) })}>Önceki</Button>}
                  <Button variant="outline" className="bg-transparent" data-testid="next-page-button" onClick={() => set({ page: String(Number(params.get("page") || 1) + 1) })}>Sonraki</Button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </Page>
  );
}
